import apiClient from '@/api/apiClient';
import { handleError, getAuthData, prepareModel } from '../../utils/storeHelpers';

export default {
  namespaced: true,
  state: {
    schema: {},
    modelSlides: [],
    isNewRecord: true,
    status: '',
  },

  getters: {
    schema: (state) => state.schema,
    modelSlides: (state) => state.modelSlides,
    isNewRecord: (state) => state.isNewRecord,
    status: (state) => state.status,
  },

  mutations: {
    SET_SCHEMA(state, schema) {
      state.schema = schema;
    },
    SET_RECORDS(state, { recordsData }) {
      state.modelSlides = Array.isArray(recordsData?.modelSlides) ? recordsData.modelSlides : [];
      state.isNewRecord = Boolean(recordsData?.isNewRecord);
    },
    SET_STATUS(state, status) {
      state.status = status;
    },
  },

  actions: {
    async fetchSchema({ commit }) {
      try {
        commit('SET_STATUS', 'loading');
        const responses = await Promise.all([
          apiClient.caxACGxPerceptionSchema(),
          apiClient.catCognitPercepCond(),
        ]);
        const [schemaRes, catCognitPercepCond] = responses.map(
          (res) => res?.data ?? {}
        );

        let id1 = 'B';
        const resCat1 = await apiClient.catLevelDescription(id1);
        const catLevelDescriptionC = resCat1.data.data;

        const resCat2 = await apiClient.catCognitSenseCond();
        const catCognitSenseCond = resCat2.data.data;

        let catSenseCondVisual = [];
        let catSenseCondAuditiva = [];
        let catSenseCondTactil = [];
        let catSenseCondOthers = [];
        for (let i = 0; i < catCognitSenseCond.length; i++) {
          var senseCategory = catCognitSenseCond[i].sense_category;
          if (senseCategory === 'Todos') {
            catSenseCondVisual.push(catCognitSenseCond[i]);
            catSenseCondAuditiva.push(catCognitSenseCond[i]);
            catSenseCondTactil.push(catCognitSenseCond[i]);
            catSenseCondOthers.push(catCognitSenseCond[i]);
          }
          if (senseCategory === 'Visual') {
            catSenseCondVisual.push(catCognitSenseCond[i]);
          }
          if (senseCategory === 'Auditiva') {
            catSenseCondAuditiva.push(catCognitSenseCond[i]);
          }
          if (senseCategory === 'Táctil') {
            catSenseCondTactil.push(catCognitSenseCond[i]);
          }
        }

        if (!schemaRes.schemaFormItems) throw new Error('Estructura de schema inválida.');

        let schema = structuredClone(schemaRes);
        schema.schemaFormItems[0].schemaForm.group1.schema.i1_cognitive_perception_level_description_id.items =
          catLevelDescriptionC;
        schema.schemaFormItems[0].schemaForm.group1.schema.i1_cognitive_perception_condition_id.items =
          catSenseCondVisual;
        schema.schemaFormItems[0].schemaForm.group1.schema.i2_cognitive_perception_level_description_id.items =
          catLevelDescriptionC;
        schema.schemaFormItems[0].schemaForm.group1.schema.i2_cognitive_perception_condition_id.items =
          catSenseCondAuditiva;
        schema.schemaFormItems[0].schemaForm.group1.schema.i3_cognitive_perception_level_description_id.items =
          catLevelDescriptionC;
        schema.schemaFormItems[0].schemaForm.group1.schema.i3_cognitive_perception_condition_id.items =
          catSenseCondTactil;
        schema.schemaFormItems[0].schemaForm.group1.schema.i4_cognitive_perception_level_description_id.items =
          catLevelDescriptionC;
        schema.schemaFormItems[0].schemaForm.group1.schema.i4_cognitive_perception_condition_id.items =
          catSenseCondOthers;
        schema.schemaFormItems[0].schemaForm.group1.schema.i5_cognitive_perception_level_description_id.items =
          catLevelDescriptionC;
        schema.schemaFormItems[0].schemaForm.group1.schema.i5_cognitive_perception_condition_id.items =
          catSenseCondOthers;
        schema.schemaFormItems[0].schemaForm.group1.schema.i6_cognitive_perception_level_description_id.items =
          catLevelDescriptionC;
        schema.schemaFormItems[0].schemaForm.group1.schema.i6_cognitive_perception_condition_id.items =
          catSenseCondOthers;
        schema.schemaFormItems[0].schemaForm.group1.schema.i7_cognitive_perception_level_description_id.items =
          catLevelDescriptionC;
        schema.schemaFormItems[0].schemaForm.group1.schema.i7_cognitive_perception_condition_id.items =
          catSenseCondOthers;
        schema.schemaFormItems[0].schemaForm.group1.schema.i8_cognitive_perception_condition_id.items =
          catCognitPercepCond.data ?? [];

        const modelFormItems = [{ modelForm: { group1: {}, group2: {} } }];

        let modelSlides = modelFormItems.map((item, i) => ({
          modelForm: item.modelForm,
          schemaForm: schema.schemaFormItems[i].schemaForm,
        }));

        const isNewRecord = true;
        const recordsData = { modelSlides, isNewRecord };
        commit('SET_SCHEMA', schema);
        commit('SET_RECORDS', recordsData);
      } catch (error) {
        handleError(error, commit);
      }
    },

    async fetchRecords({ commit, rootState, getters, dispatch }) {
      try {
        const { clinicalHistories_id, institutions_profes_id } = getAuthData(rootState);
        const res = await apiClient.modelCAxACGxPerception(clinicalHistories_id, institutions_profes_id);
        let recordsData = res?.data?.data ?? {};
        const dataModelFormItems = recordsData.modelSlides;

        if (Object.keys(getters.schema).length === 0) {
          await dispatch('fetchSchema');
        }
        const schema = JSON.parse(JSON.stringify(getters.schema));

        let modelSlides = dataModelFormItems.map((item, i) => ({
          modelForm: item.modelForm,
          schemaForm: schema.schemaFormItems[i].schemaForm,
        }));

        if (recordsData.isNewRecord === true) {
          modelSlides.physiological_aspect_id =
            dataModelFormItems[0].modelForm.physiological_aspect_id;
        }

        recordsData.modelSlides = modelSlides;
        commit('SET_RECORDS', { recordsData });
        commit('SET_STATUS', 'loaded');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async createRecord({ commit, rootState }, { model }) {
      try {
        commit('SET_STATUS', 'create');
        const preparedModel = prepareModel(rootState, model);
        await apiClient.createCAxACGxPerception(preparedModel);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async updateRecord({ commit, rootState }, { model }) {
      try {
        commit('SET_STATUS', 'update');
        const preparedModel = prepareModel(rootState, model);
        const id = preparedModel.modelSlides[0].modelForm.cognitive_aspect_id;
        await apiClient.updateCAxACGxPerception(id, preparedModel);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },
  },
};
